// CHG-1289 / ITER-3414 — v2 RBAC react-query hook.
//
// Fetches effective permissions for the authenticated user from
// GET /api/v1/users/me/permissions. The backend uses role_service's
// 60s TTL cache (_CACHE_TTL_SECONDS); the FE caches per-session via
// react-query with a 30s staleTime so we refetch before the BE cache
// might stale (half-window pattern keeps perms fresh on role changes).
//
// Org-switch invalidation: BrandContext.activeOrg change → consumers
// can call queryClient.invalidateQueries(["me", "permissions"]) (the
// OrgSwitcher CHG wires this; today the stub OrgProvider has no
// org-switch surface — when it ships, invalidation hooks into here).
//
// FE check is a UX optimization. The BE require_permission dependency
// is the source of truth; if the FE has stale permissions and the user
// calls an endpoint they can't access, the BE returns 403 (handled by
// api/client interceptor in CHG-1289).

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";

interface MePermissionsResponse {
  permissions: string[];
}

const QUERY_KEY = ["me", "permissions"] as const;

async function fetchMePermissions(): Promise<string[]> {
  const r = await apiClient().get<MePermissionsResponse>("/users/me/permissions");
  return r.data.permissions ?? [];
}

/**
 * Subscribe to the current user's effective permissions. Returns the
 * query state (loading / error / data). Use `useHasPermission(code)`
 * for the common boolean check.
 */
export function usePermissions() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchMePermissions,
    staleTime: 30_000,           // half of role_service _CACHE_TTL_SECONDS (60s) — refetches before BE cache may stale
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * True iff the user has the given permission. Returns false while the
 * query is loading or errored — gates fail closed.
 */
export function useHasPermission(code: string | undefined): boolean {
  const { data } = usePermissions();
  if (!code) return true;          // gate disabled when no code required
  if (!data) return false;          // loading / error — fail closed
  return data.includes(code);
}

/**
 * Force-refetch (e.g. on org switch). Call site:
 *   `useInvalidatePermissions()()` on org-switch handler.
 */
export function useInvalidatePermissions(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: QUERY_KEY });
  };
}
